/**
 * Copyright (c) Simam Digital. Built on Project Flowerbed
 * (Copyright (c) Meta Platforms, Inc. and affiliates).
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { COLLISION_LAYERS } from '../../Constants';
import { CollisionWorldComponent } from '../../components/ColliderComponents';
import { GameStateComponent } from '../../components/GameStateComponent';
import { InteractionSystem } from '../../lib/InteractionSystem';
import { SeedboxComponent } from '../../components/SeedboxComponents';
import { getOnlyEntity } from '../../utils/entityUtils';

const MODES = GameStateComponent.INTERACTION_MODES;

/**
 * One-tap contextual planting/harvesting for the flatscreen / mobile path.
 *
 * VR players switch modes with the in-world selection wheel; flatscreen/mobile
 * players used to have to toggle Plant/Harvest manually before a tap did
 * anything. This system removes that friction: each frame it looks at what the
 * camera crosshair is pointing at and sets the interaction mode automatically —
 *   - crosshair over a plant  → PICKING  (a tap harvests; the pick system still
 *     rejects unripe / neighbour plants with a hint)
 *   - otherwise               → PLANTING (a tap plants the selected seed)
 * so the *same single tap* (mobile action button / desktop click) does the
 * right thing. The existing PlantingSystem / PlantPickingSystem do the actual
 * work — this only chooses their mode.
 *
 * Only runs in flatscreen/emulated mode (`window.xrdevice` is set by index.js
 * when not in a native VR session), and never overrides the deliberate Water,
 * Camera, Settings or Controls modes.
 */
export class ContextualActionSystem extends InteractionSystem {
	onExecute(_delta, _time) {
		// Real VR: leave mode selection to the selection wheel.
		if (!window.xrdevice) return;

		const mode = this.gameStateComponent.interactionMode;
		// Respect deliberate / modal states — only auto-manage plant vs harvest.
		if (
			mode === MODES.WATERING ||
			mode === MODES.CAMERA ||
			mode === MODES.SETTINGS ||
			mode === MODES.CONTROLS
		) {
			return;
		}

		// Don't fight the seed shop while the player is interacting with it.
		const seedboxEntity = getOnlyEntity(this.queries.seedbox, false);
		const seedbox = seedboxEntity
			? seedboxEntity.getComponent(SeedboxComponent)
			: null;
		if (
			seedbox &&
			seedbox.inFocus &&
			(seedbox.inSelectionZone || seedbox.isPointedAt)
		) {
			return;
		}

		let desired;
		if (this._isAimingAtPlant()) {
			desired = MODES.PICKING;
		} else if (seedbox && (seedbox.plantGroups || []).length > 0) {
			desired = MODES.PLANTING;
		} else {
			desired = MODES.DEFAULT;
		}

		if (desired !== mode) {
			this.gameStateComponent.setInteractionMode(desired);
		}
	}

	/**
	 * @returns {boolean} whether the straight UI ray currently hits any plant.
	 */
	_isAimingAtPlant() {
		if (!this.uiRayComponent) return false;
		const cwEntity = getOnlyEntity(this.queries.collisionWorld, false);
		const collisionWorld = cwEntity
			? cwEntity.getComponent(CollisionWorldComponent).world
			: null;
		if (!collisionWorld) return false;
		const intersects = this.uiRayComponent.raycaster.intersectCollisionWorld(
			collisionWorld,
			{ all: COLLISION_LAYERS.PLANT },
		);
		return !!intersects;
	}
}

ContextualActionSystem.addQueries({
	collisionWorld: { components: [CollisionWorldComponent] },
	seedbox: { components: [SeedboxComponent] },
});
